import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { FACILITY_LISTING, BLOG_CATEGORY_PAGE, HOME_PAGE } from '../../src/shared/pageTypes';
import { PAGES_MENU_DISPLAY_COUNT } from '../../constants/admin';
import { isDefaultLocale } from '../../modules/i18n/locales';
import useOnUpdate from '../../modules/react/hooks/useOnUpdate';
import useEditableNavigation from '../hooks/useEditableNavigation';
import useMemoizedNavigationPageIds from '../hooks/useMemoizedNavigationPageIds';
import useSearchablePages from '../hooks/useSearchablePages';
import NavigationSettings from '../components/NavigationSettings';

const GET_NAVIGATION_TREE_DATA = gql`
  query GetCurrentSiteNavigationSettingsNavigationTreeData($locale: String!, $nonDefaultLocale: Boolean!) {
    currentSite {
      id
      navigation(locale: $locale) {
        ...NavigationSettingsNavigationItem
        page {
          ...NavigationSettingsPage
        }
      }
      primaryButton(locale: $locale) {
        ...NavigationSettingsNavigationItem
        page {
          ...NavigationSettingsPage
        }
      }
      secondaryButton(locale: $locale) {
        ...NavigationSettingsNavigationItem
        page {
          ...NavigationSettingsPage
        }
      }
    }
  }

  ${NavigationSettings.fragments.navigationItem}
  ${NavigationSettings.fragments.page}
`;

const GET_PAGES_MENU_DATA = gql`
  query GetCurrentSiteNavigationSettingsPagesMenuData(
    $excludeTypes: [String!],
    $excludeIds: [ID!],
    $first: Int!,
    $fulltext: String,
    $live: Boolean,
    $locale: String!,
    $nonDefaultLocale: Boolean!
  ) {
    pages(filter: {
      excludeTypes: $excludeTypes,
      excludeIds: $excludeIds,
      fulltext: $fulltext,
      live: $live,
      locale: $locale,
    }, first: $first) {
      edges {
        node {
          id
          ...NavigationSettingsPage
        }
      }
      totalCount
    }
  }

  ${NavigationSettings.fragments.page}
`;

const UPDATE_NAVIGATION_TREE = gql`
  mutation CurrentSiteNavigationSettingsCurrentSiteNavigationUpdate(
    $locale: String!,
    $navigation: [NavigationItemInput!]!,
    $nonDefaultLocale: Boolean!
    $primaryButton: NavigationItemInput,
    $secondaryButton: NavigationItemInput,
  ) {
    currentSiteNavigationUpdate(navigation: $navigation, locale: $locale, primaryButton: $primaryButton, secondaryButton: $secondaryButton) {
      currentSite {
        id
        navigation(locale: $locale) {
          ...NavigationSettingsNavigationItem
          page {
            ...NavigationSettingsPage
          }
        }
      }
      userErrors {
        code
        message
      }
    }
  }

  ${NavigationSettings.fragments.navigationItem}
  ${NavigationSettings.fragments.page}
`;

const propTypes = {
  code: PropTypes.string.isRequired,
  title: PropTypes.string,
};

const defaultProps = {
  title: undefined,
};

const CurrentSiteNavigationSettings = ({
  code,
  title,
  ...rest
}) => {
  const [navigationState, {
    onChange: onNavigationChange,
    onReset: onNavigationReset,
    onSubmit: onNavigationSubmit,
  }] = useEditableNavigation(
    {
      query: GET_NAVIGATION_TREE_DATA,
      options: {
        variables: {
          locale: code,
          nonDefaultLocale: !isDefaultLocale(code),
        },
        ssr: false,
        fetchPolicy: 'network-only',
      },
      dependencies: [code],
      getNavigation: (data) => {
        const { primaryButton, secondaryButton } = data.currentSite;
        let { navigation } = data.currentSite;
        if (secondaryButton) {
          navigation = [...navigation, { ...secondaryButton, isSecondary: true }];
        }

        if (primaryButton) {
          navigation = [...navigation, { ...primaryButton, isPrimary: true }];
        }

        return navigation;
      },
    },
    { mutation: UPDATE_NAVIGATION_TREE },
  );

  const {
    dirty,
    mutation: { errors, submitted, submitting },
    navigation,
    query: { loaded },
  } = navigationState;
  const memoizedPageIds = useMemoizedNavigationPageIds(navigation);

  const [pagesState, {
    onChange: onPagesSearch,
    onReset: onPagesReset,
  }] = useSearchablePages(
    GET_PAGES_MENU_DATA,
    ({ fulltext }) => ({
      variables: {
        excludeTypes: [FACILITY_LISTING, BLOG_CATEGORY_PAGE, HOME_PAGE],
        excludeIds: memoizedPageIds,
        first: PAGES_MENU_DISPLAY_COUNT,
        fulltext,
        live: true,
        locale: code,
        nonDefaultLocale: !isDefaultLocale(code),
      },
      ssr: false,
    }),
    [memoizedPageIds, loaded],
    { shouldQuery: loaded },
  );

  useOnUpdate(() => {
    onNavigationReset();
    onPagesReset();
  }, [code]);

  const { error, loading, pages, queryCount, totalCount } = pagesState;
  const enableSearch = totalCount > PAGES_MENU_DISPLAY_COUNT;

  const onReset = () => onNavigationReset({ shouldFetch: true });

  const onSubmit = () => onNavigationSubmit({
    variables: {
      locale: code,
      nonDefaultLocale: !isDefaultLocale(code),
    },
  });

  return (
    <NavigationSettings
      {...rest}
      code={code}
      enableSearch={enableSearch}
      navigation={navigation}
      navigationPageIds={memoizedPageIds}
      onNavigationChange={onNavigationChange}
      onNavigationReset={onReset}
      onNavigationSubmit={onSubmit}
      onPagesSearch={onPagesSearch}
      pages={pages}
      pagesMenuTitle={title}
      searchPagesError={error}
      searchingPages={loading}
      submittedNavigation={submitted}
      submittingNavigation={submitting}
      submitNavigationErrors={errors}
      totalPages={queryCount}
      unsubmittedNavigationChanges={dirty}
    />
  );
};

CurrentSiteNavigationSettings.propTypes = propTypes;
CurrentSiteNavigationSettings.defaultProps = defaultProps;

export default CurrentSiteNavigationSettings;
